/**
 * Choosing a tag from a /tags/recommend response.
 *
 * Its own module for the usual reason — importing resolveTerms reaches the API
 * client, Keycloak and `window` — and for one more: the tag backfill script
 * applies this same rule to lists made before resolution existed, and a script
 * scoring terms differently to the app would quietly create a second standard.
 */
import type { Candidate, SearchHit } from "./resolveTerms";

/**
 * Minimum recommendation score worth acting on.
 *
 * Measured against the live service: the correct tag for a term scores 0.6-0.82
 * ("WTI" -> WTI 0.70, "Gold" -> Gold 0.60, "EUR/USD" -> Forex 0.82), while the
 * merely-adjacent concepts it also returns sit at 0.55-0.67 (Energy,
 * Commodities, Metals & Mining). The two bands overlap, which is why a label
 * match is preferred over the score wherever one exists.
 */
const MIN_RECOMMEND_SCORE = 0.6;

/**
 * Second opinion for a term the substring search couldn't place.
 *
 * `/tags/search` matches substrings across labels and returns them ordered by
 * tag id, so a short symbol drowns: "CL" returns 486 hits led by CLP and
 * Cliffs Natural Resources, and "Gold" returns 114 led by Goldman Sachs. The
 * scoring in `bestCandidate` correctly refuses to pick from that noise, which
 * left futures and FX codes falling through to plain keywords even though the
 * tags exist — the futures codes are in the vocabulary as labels (CLc1 on WTI,
 * LCOc1 on Brent, Gcc1/XAU= on Gold), just not findable this way.
 *
 * `/tags/recommend` matches semantically and returns confidence scores, so it
 * places exactly these. Used only where the search found nothing acceptable:
 * it costs a request per unresolved term, and where the search did produce an
 * exact match that answer is already better than a scored guess.
 *
 * A tag whose own label is the term wins outright; otherwise the highest score
 * above the floor. Recommendations include contextual tags (asking about WTI
 * also returns Energy and Commodities), so an unconditional top-score pick
 * would sometimes file a term under a theme rather than the thing itself.
 */
export function pickRecommended(
  term: string,
  response: { scores?: Record<string, number>; tags?: Record<string, { category?: string; labels?: { name: string; primary?: boolean }[] }> } | undefined
): Candidate | undefined {
  const wanted = term.trim().toLowerCase();
  let best: { hit: SearchHit; score: number; exact: boolean } | undefined;

  for (const [id, tag] of Object.entries(response?.tags ?? {})) {
    const score = Number(response?.scores?.[id] ?? 0);
    const labels = tag?.labels ?? [];
    const exact = labels.some((l) => (l.name ?? "").trim().toLowerCase() === wanted);
    if (!exact && score < MIN_RECOMMEND_SCORE) continue;

    const hit: SearchHit = { id: Number(id), labels, category: tag?.category };
    if (
      !best ||
      (exact && !best.exact) ||
      (exact === best.exact && score > best.score)
    ) {
      best = { hit, score, exact };
    }
  }

  if (!best) return undefined;
  const label =
    best.hit.labels?.find((l) => l.primary)?.name ??
    best.hit.labels?.[0]?.name ??
    term;
  // "strength" drives whether alternates are offered; a scored guess is not an
  // exact match, so the user still gets to see the other candidates.
  return { hit: best.hit, strength: best.exact ? "exact" : "weak", label };
}
